import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, Smartphone, Users, Award, Wallet, Landmark, Database, FileSpreadsheet, UploadCloud, ShieldCheck, ClipboardCheck, LogOut, Menu, HandCoins } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { can, ROLE_LABEL } from "@/lib/api";

export const NAV = [
  { to: "/", label: "DBR Dashboard", hint: "Aaj ka summary", icon: LayoutDashboard, perm: "dashboard", color: "#0F172A" },
  { to: "/vi", label: "VI Virtual Account", hint: "Company balance & debit", icon: Smartphone, perm: "report:all", color: "#E60000" },
  { to: "/dse", label: "DSE Balance", hint: "Diya balance vs recovery", icon: Users, perm: "report:limited", color: "#D97706" },
  { to: "/fl", label: "FL Advance & Comm", hint: "Advance, commission, adjust", icon: Award, perm: "report:limited", color: "#0284C7" },
  { to: "/cash", label: "Cash / Bank / UPI", hint: "Actual paisa", icon: Wallet, perm: "report:all", color: "#059669" },
  { to: "/accounts", label: "Bank / UPI Accounts", hint: "Account-wise, UTR, reconcile", icon: Landmark, perm: "report:all", color: "#059669" },
  { to: "/parties", label: "Creditors & Debtors", hint: "Lena-dena, party ledger", icon: HandCoins, perm: "report:all", color: "#0F766E" },
  { to: "/masters", label: "Masters", hint: "DSE, FL, schemes", icon: Database, perm: "dashboard", color: "#475569" },
  { to: "/reports", label: "Reports", hint: "DBR, ledgers, export", icon: FileSpreadsheet, perm: "report:limited", color: "#475569" },
  { to: "/import", label: "Excel Import", hint: "Bulk upload preview", icon: UploadCloud, perm: "import:preview", color: "#475569" },
  { to: "/approvals", label: "Approvals & Audit", hint: "Reversal requests, log", icon: ClipboardCheck, perm: "reversal:request", color: "#475569" },
  { to: "/users", label: "Users", hint: "Roles & access", icon: ShieldCheck, perm: "users:manage", color: "#475569" },
];

function NavList({ onNav }) {
  const { user } = useAuth();
  return (
    <nav className="space-y-0.5">
      {NAV.filter((n) => can(user, n.perm)).map((n) => (
        <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={onNav} data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
          className={({ isActive }) => `group flex items-center gap-3 rounded-lg px-3 py-2 transition-colors duration-150 ${isActive ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
          {({ isActive }) => (
            <>
              <n.icon className="h-4 w-4 shrink-0" style={{ color: isActive ? n.color === "#0F172A" || n.color === "#475569" ? "#fff" : n.color : undefined }} />
              <span className="leading-tight">
                <span className="block text-sm font-medium">{n.label}</span>
                <span className="block text-[10px] text-slate-500 group-hover:text-slate-400">{n.hint}</span>
              </span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="px-3 pb-6">
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-md bg-[#E60000] grid place-items-center font-display font-extrabold text-white">A</div>
        <div>
          <div className="font-display font-extrabold text-white text-lg leading-none">Ayzal Traders</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFC000] mt-1">VI DBR Ledger</div>
        </div>
      </div>
    </div>
  );
}

function UserBox() {
  const { user, logout } = useAuth();
  return (
    <div className="border-t border-white/10 pt-4 px-3">
      <div className="text-sm text-white font-medium truncate" data-testid="current-user-name">{user.name}</div>
      <div className="text-[11px] text-slate-400 truncate">{user.email || user.mobile}</div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FFC000] text-slate-900 font-semibold" data-testid="current-user-role">{ROLE_LABEL[user.role]}</span>
        <button onClick={logout} className="text-slate-400 hover:text-white inline-flex items-center gap-1 text-xs" data-testid="logout-btn"><LogOut className="h-3.5 w-3.5" /> Logout</button>
      </div>
    </div>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:flex w-64 shrink-0 bg-[#0F172A] flex-col py-6 px-3 fixed inset-y-0">
        <Brand />
        <div className="flex-1 overflow-y-auto"><NavList /></div>
        <UserBox />
      </aside>
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden sticky top-0 z-40 bg-[#0F172A] text-white flex items-center justify-between px-4 h-14">
          <div className="font-display font-extrabold">Ayzal Traders <span className="text-[#FFC000] text-xs">DBR</span></div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><button data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button></SheetTrigger>
            <SheetContent side="left" className="bg-[#0F172A] border-none w-72 flex flex-col py-6 px-3">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <Brand />
              <div className="flex-1 overflow-y-auto"><NavList onNav={() => setOpen(false)} /></div>
              <UserBox />
            </SheetContent>
          </Sheet>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px]"><Outlet /></main>
      </div>
    </div>
  );
}
